import {FormActionButtons, FormattedFormRow, FormContainer, FormRowDivider} from "@dvrt/graphql-widgets";
import {Table, TableBody, TableCell, TableHead, TableRow, Typography} from "@mui/material";
import {FormattedMessage, defineMessage} from "react-intl";
import {EnumField} from "@dvrt/graphql-widgets";
import {useStyles} from "@/components/call-group/members/styles";
import {NonPaymentBlockingType} from "@/api";
import {useBoolState} from "@/hooks";
import {FormattedPageTitle} from "@/components/pages";
import {formPagesWidth} from "@/constants";
import {useHookClass} from "@dvrt/graphql-widgets";
import DomainBulkNonPaymentBlockingForm from "@/components/domain_settings/bulk_non_payment_blocking/DomainBulkNonPaymentBlockingForm";
import {showNotification} from "@/actions";
import {useDispatch} from "react-redux";
import Loading from "@/components/breadcrumbs/Loading";
import DataSource from "@/components/domain_settings/bulk_non_payment_blocking/DataSource";
import {NON_PAYMENT_BLOCKING_TYPE_MESSAGES} from "@/utils/translations";

type Props = {
    domainId: number;
};

const Content = ({domainId, dataSource}: {domainId: number; dataSource: DataSource}) => {
    const {classes, cx} = useStyles();
    const [enabled, enable, disable] = useBoolState(true);

    const form = useHookClass(DomainBulkNonPaymentBlockingForm, domainId);
    const dispatch = useDispatch();

    async function submit() {
        disable();
        if (await form.submit()) {
            dispatch(
                showNotification(
                    defineMessage({id: "save_success", defaultMessage: "Data successfully saved"}),
                    "success",
                ),
            );
            await dataSource.fetch();
        } else {
            dispatch(
                showNotification(defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}), "error"),
            );
        }
        enable();
    }

    return (
        <div style={{marginBottom: 20}}>
            <FormattedPageTitle
                desc={defineMessage({id: "bulkNonPaymentBlocking.title", defaultMessage: "Bulk non-payment blocking"})}
            />
            <FormContainer style={{maxWidth: formPagesWidth}}>
                <FormattedFormRow
                    label={defineMessage({
                        id: "bulkNonPaymentBlocking.currentSettings",
                        defaultMessage: "Current settings",
                    })}
                >
                    <Table>
                        <TableHead>
                            <TableRow className={cx(classes.row, classes.header)}>
                                <TableCell align={"center"}>
                                    <FormattedMessage
                                        id="NonPaymentBlockingType.NOT_BLOCKED"
                                        defaultMessage="Not blocked"
                                    />
                                </TableCell>
                                <TableCell align={"center"}>
                                    <FormattedMessage
                                        id="NonPaymentBlockingType.OUTGOING_BLOCKED"
                                        defaultMessage="Outgoing blocked"
                                    />
                                </TableCell>
                                <TableCell align={"center"}>
                                    <FormattedMessage
                                        id="NonPaymentBlockingType.INCOMING_AND_OUTGOING_BLOCKED"
                                        defaultMessage="Incoming and outgoing blocked"
                                    />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow className={cx(classes.row)}>
                                <TableCell align={"center"}>
                                    <Typography>{dataSource.data.current.notBlocked}</Typography>
                                </TableCell>
                                <TableCell align={"center"}>
                                    <Typography>{dataSource.data.current.outgoingBlocked}</Typography>
                                </TableCell>
                                <TableCell align={"center"}>
                                    <Typography>{dataSource.data.current.incomingAndOutgoingBlocked}</Typography>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "bulkNonPaymentBlocking.setAllTo", defaultMessage: "Set all to"})}
                >
                    <EnumField
                        value={form.values.value}
                        enum_={{NonPaymentBlockingType}}
                        onChange={(val) =>
                            form.updateValues({
                                value: val as NonPaymentBlockingType,
                            })
                        }
                        disabled={!enabled}
                        translations={NON_PAYMENT_BLOCKING_TYPE_MESSAGES}
                    />
                </FormattedFormRow>
                <FormRowDivider />
                <FormattedFormRow>
                    <FormActionButtons
                        mainAction={submit}
                        secondaryAction={() => {
                            form.reset();
                        }}
                        mainButtonEnabled={enabled}
                        secondaryButtonHidden={true}
                    />
                </FormattedFormRow>
            </FormContainer>
        </div>
    );
};

export default ({domainId}: Props) => {
    const dataSource = useHookClass(DataSource, domainId);

    if (!dataSource.loaded) return <Loading />;

    return <Content domainId={domainId} dataSource={dataSource} />;
};
